import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { GQL_UPDATE_MY_PROFILE } from '~/gql/account/account'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import { accountErrorKey } from '~/pages/account/account-errors'

/** Même borne que l'API (`account.service.ts`). */
const MAX_NAME_LENGTH = 100

type IProps = {
  account: { firstName: string, lastName: string }
}

/**
 * Prénom et nom de l'utilisateur connecté (#77). Après enregistrement, le nom
 * est aussi reporté dans le store d'auth : les menus l'affichent sans attendre
 * le prochain rafraîchissement de session.
 */
function IdentityForm({ account }: IProps) {
  const { t } = useTranslation()
  const { payload, setPayload } = useAuth()
  const [updateMyProfile, { loading }] = useMutation(GQL_UPDATE_MY_PROFILE)

  const name = z.string().trim()
    .min(1, { message: t('account.error.nameRequired') })
    .max(MAX_NAME_LENGTH, { message: t('account.error.nameTooLong') })
  const schema = z.object({ firstName: name, lastName: name })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    values: { firstName: account.firstName, lastName: account.lastName },
  })

  const onSubmit = async (values: Values) => {
    try {
      const { data } = await updateMyProfile({ variables: { input: values } })
      const saved = data?.updateMyProfile
      if (saved && payload) setPayload({ ...payload, firstName: saved.firstName, lastName: saved.lastName })
      toast.success(t('account.identity.saved'))
    } catch (error) {
      toast.error(t(accountErrorKey(error)))
    }
  }

  return (
    <Card {...tourAnchor('account.identity')}>
      <CardHeader>
        <CardTitle>{t('account.identity.title')}</CardTitle>
        <CardDescription>{t('account.identity.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <RequiredFieldsHint />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('common.firstname')}</FormLabel>
                    <FormControl><Input aria-required autoComplete="given-name" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('common.lastname')}</FormLabel>
                    <FormControl><Input aria-required autoComplete="family-name" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button type="submit" disabled={loading} className="self-start">
              {loading && <Loader className="mr-2 size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

export default IdentityForm
